import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { formatDuration } from '../../utils/formatDuration';
import { deriveReturnTimeFraction } from '../../utils/tickCountdown';
import { computeBranchTicksRemaining } from './investmentAggregation';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { StrategyGuidanceBanner } from '../strategy/StrategyGuidanceBanner';
import { NumericInput } from '../strategy/NumericInput';
import { SciencesOverviewTab } from './SciencesOverviewTab';
import type { SciencesShellProps, SciencesTabKey } from '../../pages/Science';

/** Matches Layout.tsx/AppShellLayout.tsx's existing reserve for MobileTabBar's footprint (SF-132
 *  Decision 4) — no shared named constant exists yet for this literal; each shell declares its
 *  own local copy, matching MobileBuildingsShell.tsx's precedent. */
const TAB_BAR_OFFSET = '96px';

const TABS: readonly { key: SciencesTabKey; label: string }[] = [
  { key: 'invest', label: 'Invest' },
  { key: 'overview', label: 'Overview' },
];

/**
 * SF-140: mobile card-based content for Sciences. No accordion — Sciences' investment rows are
 * flat, direct-entry, not percent/target-driven (PercentRow was evaluated and rejected — see
 * spec Codebase Context/Key Implementation Notes #2). One Buildings-style addition: a sticky
 * bottom bar, since investment is a multi-row draft like buildQty, not Explore's single scalar.
 *
 * SF-161: adds a two-tab strip (Invest/Overview) following MobileTrainingShell's
 * role="tablist"/overflow-x-auto pattern. The sticky bottom bar is additionally gated on
 * `activeTab === 'invest'` (Phase 3 Component 3) — nothing to submit from the Overview tab.
 */
export function MobileSciencesShell({
  province,
  activeStrategy,
  strategyLoading,
  activeTab,
  setActiveTab,
  researchRate,
  booksToInvest,
  isSubmittingInvestment,
  isSubmittingSettings,
  handleResearchRateChange,
  handleSubmitSettings,
  handleBooksToInvestChange,
  handleSubmitInvestment,
  handleFillStrategy,
  handleClearInvestment,
  researchRateOptions,
  scienceTypes,
  unallocatedBooks,
  investmentProgress,
  investments,
  calculatedValues,
  totalBooksToInvest,
  investmentValid,
}: SciencesShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const returnTimeFraction = deriveReturnTimeFraction(province.sync);

  const canFillStrategy = Boolean(activeStrategy?.settings?.scienceAlloc) && unallocatedBooks > 0;
  const hasActiveStrategy = Boolean(activeStrategy?.settings);

  const isOverBudget = totalBooksToInvest > unallocatedBooks;
  const bottomBarVisible = activeTab === 'invest' && totalBooksToInvest > 0;
  // SF-182 Component 3: the "Exceeds available books by N" warning line adds ~19-20px to the
  // bottom bar's rendered height that the flat 110px reserve doesn't budget for — bump only when
  // it renders (mirrors MobileRazePanel.tsx's insufficient-gold state, same shape).
  const bottomBarPaddingBottom = bottomBarVisible ? (isOverBudget ? '130px' : '110px') : undefined;

  // Phase 3 rework (Phase 2 Critical #2): investments[] is a per-tick batch list, up to 24
  // entries per branch — one row per branch, not one row per raw entry. Preserves first-seen
  // order from `investments`; falls back to the raw type string if a branch isn't present in
  // `scienceTypes` metadata (defensive — should not happen in practice).
  const investingBranches = Array.from(new Set(investments.map((inv) => inv.scienceType)));

  return (
    <div className="space-y-3" style={bottomBarPaddingBottom ? { paddingBottom: bottomBarPaddingBottom } : undefined}>
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        Sciences
      </h1>

      <StrategyGuidanceBanner activeStrategy={activeStrategy} isLoading={strategyLoading} page="science" compact />

      <div className="flex border-b overflow-x-auto" style={{ borderColor: COLORS.border }} role="tablist">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              id={`sciences-tab-${tab.key}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`sciences-panel-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className="px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
              style={{
                minHeight: 44,
                borderBottomColor: isActive ? COLORS.text.secondary : 'transparent',
                color: isActive ? COLORS.text.secondary : COLORS.text.tertiary,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div role="tabpanel" id="sciences-panel-overview" aria-labelledby="sciences-tab-overview">
          <SciencesOverviewTab province={province} investments={investments} investmentProgress={investmentProgress} compact />
        </div>
      )}

      {activeTab === 'invest' && (
      <div role="tabpanel" id="sciences-panel-invest" aria-labelledby="sciences-tab-invest" className="flex flex-col gap-3">

      <GlanceCard title="Research">
        <MetricRow
          label="Books Available"
          value={formatNumber(unallocatedBooks)}
          unit=""
          weight="primary"
          valueColor={unallocatedBooks > 0 ? COLORS.positive : undefined}
        />
        <MetricRow label="Books / Day" value={formatNumber(calculatedValues.booksPerTick)} unit="" weight="secondary" />
        <MetricRow label="Research Cost" value={formatNumber(calculatedValues.goldCost)} unit="gc" weight="secondary" />
        <div className="text-[11px] -mt-1.5" style={{ color: COLORS.text.tertiary }}>
          {calculatedValues.costPercentage.toFixed(1)}% of income
        </div>
        <MetricRow label="Daily Income" value={formatNumber(province.economy.lastTickChanges.goldIncome)} unit="gc" weight="tertiary" />
      </GlanceCard>

      <GlanceCard title="Research Rate">
        <div className="flex flex-col gap-2">
          <label htmlFor="research-rate-select" className="text-xs" style={{ color: COLORS.text.tertiary }}>
            Research Rate
          </label>
          <select
            id="research-rate-select"
            value={researchRate}
            onChange={(e) => handleResearchRateChange(e.target.value)}
            className="w-full rounded text-sm py-2 px-2"
            style={{ minHeight: 44, backgroundColor: COLORS.bg, color: COLORS.text.primary, border: `1px solid ${COLORS.inputBorder}` }}
          >
            {researchRateOptions.map((rate) => {
              const books = Math.floor(province.territory.totals.acres * rate.bpa);
              return (
                <option key={rate.value} value={rate.value}>
                  {rate.label} ({rate.bpa} BPA, {rate.gcPerBook}gc/book → {books} books)
                </option>
              );
            })}
          </select>
          <button
            onClick={handleSubmitSettings}
            disabled={isSubmittingSettings}
            className="w-full text-center py-2.5 rounded-lg text-sm font-bold disabled:opacity-50"
            style={{
              minHeight: 44,
              backgroundColor: COLORS.btnPrimary,
              border: `1px solid ${COLORS.btnPrimaryHover}`,
              color: COLORS.text.primary,
            }}
          >
            {isSubmittingSettings ? 'Updating…' : 'Update Rate'}
          </button>
        </div>
      </GlanceCard>

      {investingBranches.length > 0 && (
        <GlanceCard title="In Progress">
          {investingBranches.map((branch) => {
            const bookCount = investmentProgress[branch] ?? 0;
            const ticksRemaining = computeBranchTicksRemaining(investments, branch);
            const name = scienceTypes.find((st) => st.type === branch)?.name ?? formatEnumLabel(branch);
            const time = formatDuration(ticksRemaining, {
              returnTimeFraction,
              tickIntervalMs: province.sync.tickIntervalMs,
            });
            return (
              <div key={branch} className="flex items-center justify-between gap-2 text-xs">
                <span style={{ color: COLORS.text.primary }}>
                  {name} <span style={{ color: COLORS.gold }}>{formatNumber(Math.floor(bookCount))} books</span>
                </span>
                <span className="font-mono text-[11px]" style={{ color: COLORS.text.tertiary }}>
                  Completes in {time}
                </span>
              </div>
            );
          })}
        </GlanceCard>
      )}

      <div className="flex flex-col gap-1">
        <button
          onClick={handleFillStrategy}
          disabled={!canFillStrategy}
          className="flex items-center justify-center gap-1.5 text-sm font-semibold rounded-lg"
          style={{
            minHeight: 44,
            backgroundColor: canFillStrategy ? 'rgba(212,167,44,0.08)' : 'transparent',
            border: `1px solid ${canFillStrategy ? 'rgba(212,167,44,0.35)' : COLORS.border}`,
            color: canFillStrategy ? COLORS.gold : COLORS.text.tertiary,
            cursor: canFillStrategy ? 'pointer' : 'default',
          }}
        >
          ⚡ Fill Strategy
        </button>
        <div className="text-[11px]" style={{ color: COLORS.text.tertiary }}>
          Distributes available books toward your strategy's science targets
        </div>
        {!hasActiveStrategy && (
          <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
            No active strategy —{' '}
            <Link to="/strategy" style={{ color: COLORS.link }}>
              set one to see target guidance
            </Link>
            .
          </div>
        )}
      </div>

      <div className="rounded-lg overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
        {scienceTypes.map((scienceType) => {
          const known = province.sciences?.books?.[scienceType.type] ?? 0;
          const inProgress = investmentProgress[scienceType.type] ?? 0;

          return (
            <div
              key={scienceType.type}
              className="flex flex-col gap-1.5 px-3 py-2"
              style={{ borderBottom: `1px solid ${COLORS.border}` }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-col min-w-0">
                  <span className="text-sm" style={{ color: COLORS.text.primary }}>{scienceType.name}</span>
                  <span className="text-xs" style={{ color: COLORS.text.tertiary }}>{scienceType.effectDescription}</span>
                </div>
                <NumericInput
                  value={booksToInvest[scienceType.type as keyof typeof booksToInvest]}
                  max={unallocatedBooks}
                  onChange={(v) => handleBooksToInvestChange(scienceType.type, v)}
                  ariaLabel={`Books to invest in ${scienceType.name}`}
                  className="w-20"
                />
              </div>
              <div className="flex items-center gap-4 text-[11px]" style={{ color: COLORS.text.tertiary }}>
                <span>
                  Known <span className="font-mono" style={{ color: COLORS.text.secondary }}>{formatNumber(Math.floor(known))}</span>
                </span>
                <span>
                  In Progress{' '}
                  <span className="font-mono" style={{ color: inProgress > 0 ? COLORS.text.secondary : COLORS.text.tertiary }}>
                    {inProgress > 0 ? formatNumber(Math.floor(inProgress)) : '—'}
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
      </div>
      )}

      {bottomBarVisible && (
        <div
          data-testid="science-bottom-bar"
          className="fixed left-0 right-0 flex flex-col gap-2"
          style={{
            bottom: TAB_BAR_OFFSET,
            backgroundColor: '#0b0e19',
            borderTop: `1px solid ${COLORS.border}`,
            padding: '10px 14px',
            paddingBottom: `calc(10px + env(safe-area-inset-bottom))`,
            zIndex: 30,
          }}
        >
          <div className="flex items-baseline justify-between text-[11px]" style={{ color: COLORS.text.tertiary }}>
            <span>
              Investing{' '}
              <b className="font-mono" style={{ color: isOverBudget ? COLORS.negative : COLORS.text.primary }}>
                {formatNumber(totalBooksToInvest)} books
              </b>{' '}
              · {formatNumber(unallocatedBooks)} available
            </span>
          </div>
          {isOverBudget && (
            <div className="text-[11px]" style={{ color: COLORS.negative }}>
              Exceeds available books by {formatNumber(totalBooksToInvest - unallocatedBooks)}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleClearInvestment}
              className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold"
              style={{ border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary, minHeight: 44 }}
            >
              Clear
            </button>
            <button
              onClick={handleSubmitInvestment}
              disabled={isSubmittingInvestment || !investmentValid}
              className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: COLORS.btnPrimary, border: `1px solid ${COLORS.btnPrimaryHover}`, color: COLORS.text.primary, minHeight: 44 }}
            >
              {isSubmittingInvestment ? 'Investing…' : 'Invest'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
